import { Controller } from '@hotwired/stimulus';
import { AutomationRules } from '../lib/automation_rules.js';

// Edits one draft condition. The parent page owns the saved automation.
export default class extends Controller {
  static targets = ['targetType', 'characterStep', 'characterChecks', 'campaignChecks', 'campaignProperty', 'selectionMode', 'characterPicker', 'groupStep', 'group', 'groupMatch',
    'characters', 'property', 'comparison', 'details', 'conditionFields', 'turnFields', 'combatFields', 'focusFields', 'positionFields', 'conditions', 'turn', 'combat', 'focused', 'position', 'error'];
  static values = { schema: Object };

  connect() {
    this.showTarget();
    this.dispatch('ready');
  }

  get campaignTarget() { return this.targetTypeTargets.find(input => input.checked)?.value === 'campaign'; }
  get property() { return (this.campaignTarget ? this.campaignPropertyTargets : this.propertyTargets).find(input => input.checked)?.value; }
  get usesGroup() { return !this.campaignTarget && this.selectionModeTargets.find(input => input.checked)?.value === 'group'; }

  get selectedTargets() {
    if (this.campaignTarget) return this.schemaValue.targets.filter(target => target.variable.startsWith('campaign.') && target.field === this.property);
    if (this.usesGroup) return this.schemaValue.targets.filter(target => target.group === this.groupTarget.value && target.field === this.property);
    return [...this.charactersTarget.selectedOptions].map(option =>
      this.schemaValue.targets.find(target => target.character === option.value && target.field === this.property));
  }

  get valueOptions() {
    return this.property === 'conditions' ? [...this.conditionsTarget.options] : this[`${this.property}Targets`];
  }

  get selectionAttribute() { return this.property === 'conditions' ? 'selected' : 'checked'; }

  populate(event) {
    const statement = event.detail.statement;
    if (!statement) return;
    const targets = AutomationRules.targetsFor(statement).map(variable => this.schemaValue.targets.find(target => target.variable === variable));
    if (targets.some(target => !target)) {
      this.errorTarget.textContent = 'A selected character is no longer available. Choose characters to replace it.';
      return;
    }
    for (const input of this.targetTypeTargets) input.checked = input.value === (targets[0].variable.startsWith('campaign.') ? 'campaign' : 'character');
    for (const input of this.selectionModeTargets) input.checked = input.value === (targets[0].group ? 'group' : 'characters');
    if (targets[0].group) {
      this.groupTarget.value = targets[0].group;
      for (const input of this.groupMatchTargets) input.checked = input.value === statement.groupMatch;
    }
    for (const option of this.charactersTarget.options) option.selected = targets.some(target => target.character === option.value);
    for (const input of this.campaignTarget ? this.campaignPropertyTargets : this.propertyTargets) input.checked = input.value === targets[0].field;
    if (targets[0].field === 'conditions') {
      for (const input of this.comparisonTargets) input.checked = input.value === statement.comparison;
    }
    for (const option of this.valueOptions) option[this.selectionAttribute] = false;
    for (const value of statement.values) {
      let option = this.valueOptions.find(option => option.dataset.ruleValue === JSON.stringify(value));
      if (!option && this.property === 'conditions') {
        option = new Option(`Missing value: ${value?.var || JSON.stringify(value)}`, JSON.stringify(value));
        option.dataset.ruleValue = JSON.stringify(value);
        this.conditionsTarget.add(option);
      }
      if (option) option[this.selectionAttribute] = true;
    }
    // Refresh Select2 through its native-event bridge without coupling controllers.
    this.charactersTarget.dispatchEvent(new Event('change', { bubbles: true }));
    this.showTarget();
  }

  showTarget() {
    this.characterStepTarget.hidden = this.campaignTarget;
    this.characterPickerTarget.hidden = this.usesGroup;
    this.groupStepTarget.hidden = !this.usesGroup;
    this.characterChecksTarget.hidden = this.campaignTarget;
    this.campaignChecksTarget.hidden = !this.campaignTarget;
    this.detailsTarget.hidden = !this.campaignTarget && !this.usesGroup && this.charactersTarget.selectedOptions.length === 0;
    this.conditionFieldsTarget.hidden = this.property !== 'conditions';
    this.turnFieldsTarget.hidden = this.property !== 'turn';
    this.combatFieldsTarget.hidden = this.property !== 'combat';
    this.focusFieldsTarget.hidden = this.property !== 'focused';
    this.positionFieldsTarget.hidden = this.property !== 'position';
    this.errorTarget.textContent = '';
  }

  save(event) {
    try {
      const targets = this.selectedTargets;
      if (!targets.length) throw new Error('Choose a character first.');
      if (targets.some(target => !target)) throw new Error('Choose available characters.');
      const choices = this.valueOptions;
      const statement = { ...(targets.length === 1 ? { target: targets[0].variable } : { targets: targets.map(target => target.variable) }),
        ...(this.usesGroup ? { groupMatch: this.groupMatchTargets.find(input => input.checked)?.value } : {}),
        comparison: this.property === 'conditions' ? this.comparisonTargets.find(input => input.checked)?.value : this.property === 'position' ? 'one_of' : 'is',
        values: choices.filter(option => option[this.selectionAttribute]).map(option => JSON.parse(option.dataset.ruleValue)) };
      // Async choices can include conditions created since the page was loaded.
      const options = choices.filter(option => this.property !== 'conditions' || option.dataset.conditionId)
        .map(option => ({ value: JSON.parse(option.dataset.ruleValue), label: option.getAttribute?.('aria-label') || option.textContent }));
      const variables = new Set(targets.map(target => target.variable));
      const schema = { ...this.schemaValue, targets: this.schemaValue.targets.map(item => variables.has(item.variable) ? { ...item, values: options.map(option => option.value) } : item) };
      AutomationRules.encode('and', [statement], schema);
      this.dispatch('save', { detail: { statement, options } });
    } catch (error) {
      event.stopImmediatePropagation(); // Invalid drafts must keep the shared drawer open.
      this.errorTarget.textContent = error.message;
    }
  }
}
